<script lang="ts">
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import { Alert, Button, toast } from '@Shelly/shelly-design-system';
	import FactoryResetModal from 'src/components/FactoryResetModal.svelte';
	import { deleteDevice } from 'src/helpers/add_delete_helpers';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { getRoomZWaveCloudDevices } from 'src/helpers/z_wave_gateway_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { devices, devices_status } from 'src/stores';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	$: status = $devices_status?.[real_id(device?.id)];
	$: isOnline = status?.online;

	let showModal = false;
	let loading = false;

	const hardResetNetwork = async () => {
		if (loading) return;
		loading = true;

		const res = await gen2Request(device, 'Zwave.HardReset', {});

		if (!res?.isok) {
			toast.open({
				type: 'danger',
				title: $format('zwave_hard_reset_err_toast', {
					default: 'Failed to reset Z-Wave network.',
				}),
			});
			loading = false;
			return;
		}

		const roomDevices = getRoomZWaveCloudDevices(device, get(devices));
		for (const zwDevice of roomDevices) {
			await deleteDevice(real_id(zwDevice.id));
		}

		toast.open({
			type: 'success',
			title: $format('zwave_hard_reset_scc_toast', {
				default: 'Z-Wave network was reset.',
			}),
		});
		resetDrawerClose();
		loading = false;
	};

	$: modalProps = {
		title: $format('prompt_title_zwave_hard_reset', {
			default: 'Are you sure?',
		}),
		name: device?.name,
		text: $format('prompt_text_zwave_hard_reset', {
			default: 'This action cannot be undone.',
		}),
		alert: {
			heading: $format('alert_heading_zwave_hard_reset_warning', {
				default: 'All paired devices will be removed.',
			}),
			type: 'danger' as const,
		},
		button_1: $format('button_zwave_hard_reset', {
			default: 'Reset network',
		}),
		button_1_variant: 'danger' as const,
		button_2: $format('button_cancel', { default: 'Cancel' }),
		callback: hardResetNetwork,
	};
</script>

<Alert
	class="mb-s"
	type="warning"
	isHeadingBold
	heading={capFirstChar(
		$format('alert_heading_zwave_hard_reset', {
			default: 'Irreversible action',
		}),
	)}
	message={$format('alert_message_zwave_hard_reset', {
		default:
			'This erases the Z-Wave network on this gateway and removes all paired devices from your account.',
	})}
/>

<Button
	{loading}
	disabled={!isOnline}
	size="m"
	width="full"
	variant="danger"
	on:click={() => (showModal = true)}
>
	{capFirstChar(
		$format('button_zwave_hard_reset', {
			default: 'Reset network',
		}),
	)}
</Button>

{#if showModal}
	<FactoryResetModal bind:showModal props={modalProps} />
{/if}
